import React from 'react';
import { useSelector } from 'react-redux';
import { useNoiseListener } from '../../ecg/hooks/useNoiseListener';
import { leadNamesSelector } from '../../ecg/selectors/signal.selectors';
import { Ruler } from '../../ruler/components/Ruler';
import { RulerContainer } from '../../ruler/components/RulerContainer';
import { Plot } from '../contexts/plot.context';
import { Row } from '../contexts/row.context';
import { Strip } from '../contexts/strip.context';
import {
  NOISE,
  squareToPixel,
  TOP_PLOT_HEADER_HEIGHT,
} from '../utils/graph.utils';
import { AxisContainer } from './AxisContainer';
import { EditableStrip } from './EditableStrip';
import { GainPickerContainer } from './GainPickerContainer';
import { GridContainer } from './GridContainer';
import { ManualNoiseBar } from './ManualNoiseBar';
import { NoiseBar } from './NoiseBar';
import { NoiseWaves } from './NoiseWaves';
import { STRIP_EDITOR_WIDTH, StripEditor } from './StripEditor';
import { StripPosition } from './StripPosition';
import { DefaultLeads } from './DefaultLeads';
import { PlotContainer } from './PlotContainer';
import { MinutePlot } from './MinutePlot';
import { PlotHeader } from './PlotHeader';
import {
  RULER_HEADER_HEIGHT,
  RulerInfo,
} from '../../ruler/components/RulerInfo';
import { Tool } from '../../tool/utils/tool.utils';
import { isToolActiveSelector } from '../../tool/selectors/tool.selectors';
import { StripContent } from './StripContent';
import { LaddergramContainer } from './LaddergramContainer';
import { booleanPreferenceSelector } from '../../auth/selectors/auth.selectors';

const STRIP_HEIGHT = squareToPixel(2);
const NOISE_PLOT_DURATION = 20_000; // 20s

const getStripId = (key: number | string) => `${NOISE}-strip-${key}`;

export const NoisePlot: React.FC = () => {
  useNoiseListener();
  const leads = useSelector(leadNamesSelector);
  const isRulerActive = useSelector(isToolActiveSelector(Tool.ruler));
  const showLaddergram = useSelector(
    booleanPreferenceSelector('showLaddergram'),
  );

  return (
    <PlotContainer>
      <Strip id={getStripId('root')} duration={NOISE_PLOT_DURATION}>
        <Plot>
          <PlotHeader offset={STRIP_EDITOR_WIDTH} height={RULER_HEADER_HEIGHT}>
            <RulerInfo />
          </PlotHeader>
        </Plot>
        <Plot plotName='noise' cursor={isRulerActive ? 'crosshair' : undefined}>
          {leads.map((lead, index) => (
            <Strip
              key={index}
              id={getStripId(index)}
              duration={NOISE_PLOT_DURATION}
              leads={[lead]}
            >
              <Row
                height={STRIP_HEIGHT}
                topOffset={index === 0 ? TOP_PLOT_HEADER_HEIGHT : undefined}
              >
                <StripEditor>
                  <GainPickerContainer />
                </StripEditor>
                <EditableStrip>
                  <GridContainer />
                  <StripPosition>
                    <StripContent clipType='plot'>
                      <DefaultLeads />
                    </StripContent>
                    <NoiseWaves />
                    <ManualNoiseBar />
                    <RulerContainer>
                      <Ruler />
                    </RulerContainer>
                  </StripPosition>
                </EditableStrip>
              </Row>
            </Strip>
          ))}
        </Plot>
        <Plot>
          <NoiseBar offset={STRIP_EDITOR_WIDTH} />
          <AxisContainer offset={STRIP_EDITOR_WIDTH} />
        </Plot>
      </Strip>
      <MinutePlot>
        {showLaddergram && (
          <Plot>
            <LaddergramContainer />
          </Plot>
        )}
      </MinutePlot>
    </PlotContainer>
  );
};
